import type { ReactNode } from 'react'
import { Button, ReceiptsTable, StatusMessage, Tabs, panelId, tabId, type ReceiptsTableRow } from '@krp/ui-kit'
import type { AnalyticsPage, AnalyticsReceipt } from '../../cashier-analytics/model/types'
import { receiptColor } from '../../cashier-analytics/model/analytics'
import { correctionCountedText, correctionSettlementText, problemText, type SalesPointProblem } from '../model/format'
import type { SalesPointReceiptsKind } from '../model/types'

export interface ShopDayReceiptsProps {
  timeZoneId: string
  /** Открытая вкладка: «Чеки» или «Чеки коррекции»; страница `page` — её. */
  kind: SalesPointReceiptsKind
  selectKind: (kind: SalesPointReceiptsKind) => void
  page: AnalyticsPage | undefined
  busy: boolean
  error: SalesPointProblem | null
  more: () => void
  /** «Подробнее»: открывает чек в окне его источника поверх статистики по адресу, который отдала служба. */
  open: (originalPath: string) => void
  /** Отказ открыть чек: источник не зарегистрировал окно чека. */
  openError: string | null
  /** Товары чека под раскрытой строкой (их читает binding источника); нет — строки не раскрываются. */
  receiptLines?: (item: AnalyticsReceipt) => ReactNode
}

/** Строка чека дня; у чека коррекции ещё тип расчёта и «в итогах» словами (у обычного чека — пусто, столбцов нет). */
interface ShopReceiptRow extends ReceiptsTableRow { item: AnalyticsReceipt; settlement: string; counted: string }

const TABS_PREFIX = 'shop-day-receipts'
const TABS: readonly { id: SalesPointReceiptsKind; label: string }[] = [{ id: 'receipts', label: 'Чеки' }, { id: 'corrections', label: 'Чеки коррекции' }]

const timeOf = (at: string | null, timeZoneId: string) =>
  at === null ? 'не указано' : new Date(at).toLocaleTimeString('ru-RU', { timeZone: timeZoneId, hour: '2-digit', minute: '2-digit' })

const rowOf = (item: AnalyticsReceipt, timeZoneId: string): ShopReceiptRow => {
  const correction = item.receipt.correction ?? null
  const settlement = correction === null ? '' : correctionSettlementText(correction)
  return {
    id: `${item.resourceId}:${item.receipt.key}`, item, refund: item.receipt.isRefund, number: item.receipt.number,
    at: timeOf(item.receipt.at, timeZoneId), register: item.receipt.registerName, cashier: item.receipt.cashierName,
    total: item.receipt.amount, cash: item.receipt.cashAmount, card: item.receipt.cardAmount,
    settlement, counted: correction === null ? '' : correctionCountedText(correction),
    ...(correction === null ? {} : { kindLabel: `Коррекция · ${settlement}`, muted: !correction.counted }),
  }
}

const sourceOf = (row: ShopReceiptRow) => ({ name: row.item.resourceName, color: receiptColor(row.item.resourceId, row.item.receipt.registerKey) })

/**
 * Чеки магазина за один день — общей таблицей чеков, двумя вкладками (RP-ST17). «Чеки»: нажатие раскрывает товары чека, «Подробнее»
 * открывает окно чека источника. «Чеки коррекции»: своего окна у коррекции нет — строки только для чтения, с типом расчёта и тем, вошёл ли
 * чек в итоги дня (неучтённые приглушены). Заголовок считает чеки открытой вкладки.
 */
export function ShopDayReceipts(props: ShopDayReceiptsProps) {
  const rows = (props.page?.items ?? []).map((item) => rowOf(item, props.timeZoneId))
  const receiptLines = props.receiptLines
  const corrections = props.kind === 'corrections'
  return <section className="sales-points__receipts" aria-label="Чеки за день" aria-busy={props.busy}>
    <h3>Чеки за день{props.page === undefined ? '' : `: ${props.page.total}`}</h3>
    <Tabs label="Чеки за день" idPrefix={TABS_PREFIX} items={TABS} selected={props.kind}
      onSelect={(id) => props.selectKind(id === 'corrections' ? 'corrections' : 'receipts')} />
    <div role="tabpanel" id={panelId(TABS_PREFIX, props.kind)} aria-labelledby={tabId(TABS_PREFIX, props.kind)}>
      {props.error !== null && <StatusMessage kind="error" title={corrections ? 'Не удалось получить чеки коррекции' : 'Не удалось получить чеки'} text={problemText(props.error)} />}
      {!corrections && props.openError !== null && <StatusMessage kind="error" title="Не удалось открыть чек" text={props.openError} />}
      {props.busy && rows.length === 0 && <p role="status">{corrections ? 'Загружаем чеки коррекции…' : 'Загружаем чеки…'}</p>}
      {props.page?.total === 0 && <p>{corrections ? 'За этот день чеков коррекции нет.' : 'За этот день чеков нет.'}</p>}
      {rows.length > 0 && (corrections
        ? <ReceiptsTable label="Чеки коррекции магазина за день" rows={rows} source={sourceOf} columns={[
          { id: 'settlement', label: 'Тип расчёта', cell: (row) => row.settlement, title: (row) => row.settlement },
          { id: 'counted', label: 'В итогах', cell: (row) => row.counted, title: (row) => row.counted },
        ]} />
        : <ReceiptsTable label="Чеки магазина за день" rows={rows} source={sourceOf} onDetails={(row) => props.open(row.item.receipt.originalPath)}
          {...(receiptLines === undefined ? {} : { renderLines: (row: ShopReceiptRow) => receiptLines(row.item) })} />)}
      {props.page?.nextOffset != null && <Button actionId="receipts.sales-points.more" busy={props.busy} onClick={props.more}>Показать ещё</Button>}
    </div>
  </section>
}
